import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Wrench, Sparkles, Copy, Check, Play, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePlayer } from "@/context/PlayerContext";
import { api } from "@/lib/api";
import { share, reportShareToast } from "@/lib/share";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Editor() {
  const { t } = useTranslation();
  const { player } = usePlayer();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [hint, setHint] = useState("");
  const [eraA, setEraA] = useState("antiquity");
  const [eraASlot, setEraASlot] = useState("");
  const [eraAPiece, setEraAPiece] = useState("");
  const [eraB, setEraB] = useState("future");
  const [eraBSlot, setEraBSlot] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [mine, setMine] = useState([]);

  const ERA_CHOICES = [
    { key: "antiquity",   label: t("eras.antiquity"),    color: "text-amber-300" },
    { key: "medieval",    label: t("eras.medieval"),     color: "text-emerald-300" },
    { key: "renaissance", label: t("eras.renaissance"),  color: "text-rose-300" },
    { key: "future",      label: t("eras.future"),       color: "text-teal-300" },
  ];

  useEffect(() => {
    if (!player?.id) return;
    api.get(`/custom-levels/by-creator/${player.id}`).then((r) => setMine(r.data)).catch(() => {});
  }, [player, created]);

  const handleCreate = async () => {
    if (!player?.id) { toast.error(t("editor.need_profile")); return; }
    if (!title.trim()) { toast.error(t("editor.title_missing")); return; }
    if (eraA === eraB) { toast.error(t("editor.select_two_eras")); return; }
    try {
      const { data } = await api.post(`/custom-levels`, {
        creator_player_id: player.id,
        title, hint,
        era_a: eraA, era_a_slot_label: eraASlot, era_a_piece_label: eraAPiece,
        era_b: eraB, era_b_slot_label: eraBSlot,
      });
      setCreated(data);
      toast.success(t("editor.created_ok", { code: data.share_code }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("family.generic_error"));
    }
  };

  const copyShare = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!player) {
    return (
      <div className="min-h-screen hero-radial grain flex items-center justify-center p-6">
        <div className="glass-strong rounded-3xl p-8 max-w-md text-center">
          <Wrench className="w-10 h-10 text-amber-300 mx-auto mb-3" />
          <h2 className="font-serif text-2xl font-bold">{t("editor.no_profile_title")}</h2>
          <p className="text-stone-300 mt-2">{t("editor.no_profile_text")}</p>
          <Link to="/profile" className="inline-block mt-4 text-amber-300 hover:text-amber-200 font-bold">{t("editor.no_profile_cta")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen hero-radial grain">
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/levels" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-levels">
          <ChevronLeft className="w-4 h-4" /> {t("common.back_levels")}
        </Link>
        <div className="flex items-center gap-2 font-serif text-lg">
          <Wrench className="w-5 h-5 text-amber-300" /> {t("editor.header")}
        </div>
        <LanguageSwitcher />
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 sm:px-10 pb-20 space-y-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300/90 font-bold">{t("editor.kicker")}</div>
          <h2 className="mt-1 font-serif text-4xl sm:text-5xl font-black text-stone-50">
            {t("editor.title_1")}<span className="italic text-amber-300">{t("editor.title_2")}</span>
          </h2>
          <p className="mt-2 text-stone-300">{t("editor.subtitle")}</p>
        </motion.div>

        <section className="glass rounded-3xl p-6 space-y-4" data-testid="editor-form">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-stone-300 mb-1">{t("editor.title_input")}</label>
            <Input value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} placeholder={t("editor.title_placeholder")} className="bg-black/40 border-white/15" data-testid="input-title" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-stone-300 mb-1">{t("editor.hint_input")}</label>
            <Input value={hint} maxLength={200} onChange={(e) => setHint(e.target.value)} placeholder={t("editor.hint_placeholder")} className="bg-black/40 border-white/15" data-testid="input-hint" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <div className="rounded-2xl border border-amber-400/25 p-4 bg-amber-900/10">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2">{t("editor.era_a_kicker")}</div>
              <div className="flex flex-wrap gap-2 mb-3">
                {ERA_CHOICES.map((e) => (
                  <button key={e.key}
                    onClick={() => setEraA(e.key)}
                    data-testid={`era-a-${e.key}`}
                    className={`px-2 py-1 rounded-full text-xs font-bold ${eraA === e.key ? "bg-amber-400 text-stone-950" : "bg-black/40 border border-white/15 text-stone-300"}`}
                  >{e.label}</button>
                ))}
              </div>
              <Input value={eraAPiece} maxLength={40} onChange={(e) => setEraAPiece(e.target.value)} placeholder={t("editor.piece_placeholder")} className="bg-black/40 border-white/15 mb-2" data-testid="input-piece" />
              <Input value={eraASlot}  maxLength={40} onChange={(e) => setEraASlot(e.target.value)}  placeholder={t("editor.slot_a_placeholder")} className="bg-black/40 border-white/15" data-testid="input-slot-a" />
            </div>

            <div className="rounded-2xl border border-teal-400/25 p-4 bg-teal-900/10">
              <div className="text-[10px] uppercase tracking-widest text-teal-300 font-bold mb-2">{t("editor.era_b_kicker")}</div>
              <div className="flex flex-wrap gap-2 mb-3">
                {ERA_CHOICES.map((e) => (
                  <button key={e.key}
                    onClick={() => setEraB(e.key)}
                    data-testid={`era-b-${e.key}`}
                    className={`px-2 py-1 rounded-full text-xs font-bold ${eraB === e.key ? "bg-teal-400 text-stone-950" : "bg-black/40 border border-white/15 text-stone-300"}`}
                  >{e.label}</button>
                ))}
              </div>
              <Input value={eraBSlot} maxLength={40} onChange={(e) => setEraBSlot(e.target.value)} placeholder={t("editor.slot_b_placeholder")} className="bg-black/40 border-white/15" data-testid="input-slot-b" />
              <div className="mt-3 text-xs text-teal-200/80 italic">{t("editor.era_b_note")}</div>
            </div>
          </div>

          <Button onClick={handleCreate} className="w-full bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full py-5" data-testid="btn-create-puzzle">
            <Sparkles className="w-4 h-4 mr-2" /> {t("editor.create")}
          </Button>

          {created && (
            <div className="mt-2 rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4 flex items-center gap-3" data-testid="created-puzzle">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">{t("editor.share_code")}</div>
                <div className="font-mono text-lg text-stone-50">{created.share_code}</div>
              </div>
              <button onClick={() => copyShare(created.share_code)} className="text-stone-300 hover:text-stone-100" data-testid="copy-share-code">
                {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  const url = `${window.location.origin}/play-custom/${created.share_code}`;
                  const r = await share({
                    title: `Chronos Time Puzzle — ${created.title}`,
                    text: t("share.editor_text", { title: created.title }),
                    url,
                  });
                  reportShareToast(r, t);
                }}
                className="border-fuchsia-400/50 bg-transparent text-fuchsia-200 hover:bg-fuchsia-900/20"
                data-testid="btn-share-puzzle"
              >
                <Share2 className="w-3.5 h-3.5 mr-1" /> {t("share.share")}
              </Button>
              <Button size="sm" onClick={() => navigate(`/play-custom/${created.share_code}`)} className="bg-teal-500 hover:bg-teal-400 text-stone-950 font-bold" data-testid="btn-play-created">
                <Play className="w-3.5 h-3.5 mr-1" /> {t("editor.play_created")}
              </Button>
            </div>
          )}
        </section>

        {mine.length > 0 && (
          <section className="glass rounded-3xl p-6" data-testid="my-puzzles">
            <h3 className="font-serif text-2xl font-bold text-stone-50 mb-4">{t("editor.mine_title")}</h3>
            <div className="space-y-2">
              {mine.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-xl bg-black/30 border border-white/10 px-3 py-2">
                  <div>
                    <div className="font-serif text-lg text-stone-50">{p.title}</div>
                    <div className="text-xs text-stone-400">{t("editor.plays_label", { code: p.share_code, n: p.plays })}</div>
                  </div>
                  <Button size="sm" onClick={() => navigate(`/play-custom/${p.share_code}`)} variant="outline" className="border-white/20 bg-transparent text-stone-200">
                    {t("editor.play_created")}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        <PlayByCode />
      </main>
    </div>
  );
}

function PlayByCode() {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  return (
    <section className="glass rounded-3xl p-6" data-testid="play-by-code">
      <h3 className="font-serif text-2xl font-bold text-stone-50 mb-3">{t("editor.play_by_code_title")}</h3>
      <p className="text-sm text-stone-400 mb-3">{t("editor.play_by_code_desc")}</p>
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("editor.play_by_code_placeholder")} className="bg-black/40 border-white/15 font-mono uppercase" data-testid="input-play-code" />
        <Button onClick={() => code.trim() && navigate(`/play-custom/${code.trim().toUpperCase()}`)} className="bg-fuchsia-500 hover:bg-fuchsia-400 text-stone-950 font-bold" data-testid="btn-play-code">
          <Play className="w-4 h-4 mr-2" /> {t("editor.play_by_code_open")}
        </Button>
      </div>
    </section>
  );
}
